import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";

import { SparklesIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRellenarDescripcionConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import {
  IMAGENES_DEL_EXPEDIENTE,
  urlDeLaImagenDelExpediente,
  urlDeLaImagenDelRol,
  type GuardarDescripcionInput,
  type Informe,
  type RolDeInforme,
  type TipoDeImagenDelInforme,
} from "@/lib/informes";
import { cn } from "@/lib/utils";

import { BarraDeGuardado } from "./BarraDeGuardado";
import { CampoConAsistencia } from "./CampoConAsistencia";
import {
  contarLegalesCambiados,
  descartarLaPropuestaLegal,
  legalesAPayload,
  legalesDelRol,
  legalesVacios,
  mismosLegales,
  quedaAlgoDeLoLegalPropuesto,
  sumarLaPropuestaLegal,
  type CampoLegal,
  type DatosLegalesDelRol,
  type EscritoLegal,
  type LegalesPorRol,
} from "./datosLegalesDelRol";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import {
  descartarLaPropuestaDeDescripcion,
  resultadoAlGuardar,
  resumenDeLoPropuestoEnDescripcion,
  sumarLaPropuestaDeDescripcion,
  type DeslindesDelRol,
  type DeslindesPorRol,
  type EscritoDeDescripcion,
} from "./descripcionConIa";
import { IndiceLateral } from "./IndiceDeLaPestana";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import { PildoraDeValidacion } from "./PildoraDeValidacion";
import { AvisoDeLoRellenado, BotonDeRellenoConIa, type AvisoDelRelleno } from "./RellenoConIa";
import { AnexosDelInforme } from "./AnexosDelInforme";
import { TarjetaDeImagen, motivoDeRechazoDeLaImagen } from "./TarjetaDeImagen";
import {
  SECCION_DEL_ENCUADRE,
  avanceDeDescripcion,
  entradasDelIndiceDeDescripcion,
  idDelRolDeDescripcion,
  idDeLaSeccionDeDescripcion,
  pendientesDeDescripcion,
  seccionesDeDescripcionCompletas,
  type EntradaDeDescripcion,
} from "./indiceDeLaDescripcion";

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDeDescripcionHandle {
  /** Guarda, igual que apretar el botón de la pestaña. */
  guardar: () => void;
  /** Toma lo que hay en pantalla como "lo guardado" y apaga el aviso. */
  marcarGuardado: () => void;
}

interface PestanaDeDescripcionProps {
  informe: Informe;
  guardando: boolean;
  /** Qué imagen tiene una subida o un borrado en curso, o `null`. */
  ocupadaEn: string | null;
  ref?: Ref<PestanaDeDescripcionHandle>;
  /** Avisa si hay cambios sin guardar en esta pestaña. */
  onCambios?: (hayCambios: boolean) => void;
  onImagenDelRol: (rolId: number, archivo: File) => void;
  onQuitarImagenDelRol: (rolId: number) => void;
  onImagenDelExpediente: (tipo: TipoDeImagenDelInforme, archivo: File) => void;
  onQuitarImagenDelExpediente: (tipo: TipoDeImagenDelInforme) => void;
  /** Guarda los roles (deslindes y datos legales), las notas y el semestre del avalúo. */
  onGuardar: (
    roles: GuardarDescripcionInput["roles"],
    notas: string,
    avaluoFiscalPeriodo: string | null,
  ) => void;
}

/**
 * La pestaña "Descripción de la propiedad": un bloque de límite por cada rol
 * del informe, más los tres encuadres fijos del predio.
 *
 * EL PRIMER BLOQUE ES UNO POR ROL y los otros tres son fijos, y esa asimetría
 * es el punto de la pantalla: el deslinde es del ROL —un informe con nueve
 * roles tiene nueve capturas del KMZ— mientras que "por dónde se llega" y "qué
 * ciudades hay alrededor" son del PREDIO y se responden una sola vez.
 *
 * LAS IMÁGENES SE GUARDAN SOLAS, al soltarlas, y el botón "Guardar cambios"
 * solo escribe TEXTO. No es una inconsistencia: una imagen es una petición con
 * su archivo, así que meterla en el guardado del formulario obligaría a
 * reenviar todas las imágenes cada vez que alguien corrige una coma. Por eso
 * tampoco cuentan como "cambios sin guardar": cuando el aviso aparece, ya
 * están en el servidor.
 *
 * LA CASILLA "Información validada" VA POR ROL y no al pie, aunque el prototipo
 * dibuje una sola abajo. Con nueve roles, una casilla al pie no diría cuál de
 * las nueve descripciones se revisó — y eso es exactamente lo que la casilla
 * existe para garantizar, porque el texto lo puede haber propuesto la IA. Es el
 * mismo criterio que ya tienen las dos de Antecedentes generales.
 *
 * SÉPTIMA Y ÚLTIMA PESTAÑA CON "Rellenar con IA". A diferencia de sus seis
 * hermanas —que proponen un cuadro nuevo— esta propone texto para roles que
 * YA EXISTEN: nunca crea, borra ni edita un rol, y sin roles el botón queda
 * deshabilitado porque no hay a quién escribirle. La mezcla vive en
 * {@see sumarLaPropuestaDeDescripcion}, que solo escribe en campos vacíos —
 * mismo criterio que los dos párrafos de suelos— y por eso "Descartar" SÍ
 * puede deshacer un texto acá, al revés que en suelos: ver el encabezado de
 * `descripcionConIa.ts`.
 */
export function PestanaDeDescripcion({
  informe,
  guardando,
  ocupadaEn,
  ref,
  onCambios,
  onImagenDelRol,
  onQuitarImagenDelRol,
  onImagenDelExpediente,
  onQuitarImagenDelExpediente,
  onGuardar,
}: PestanaDeDescripcionProps) {
  const roles = informe.roles ?? [];

  const [deslindes, setDeslindes] = useState<DeslindesPorRol>(() => deslindesDesde(roles));
  const [notas, setNotas] = useState(informe.notas?.descripcion ?? "");

  /* LOS DATOS LEGALES de cada rol y el semestre del avalúo (uno por informe).
     Se guardan con el mismo botón que los deslindes: son texto. */
  const [legales, setLegales] = useState<LegalesPorRol>(() => legalesDesde(roles));
  const [periodo, setPeriodo] = useState(informe.avaluo_fiscal_periodo ?? "");
  const [erroresLegales, setErroresLegales] = useState<Record<number, Partial<Record<CampoLegal, string>>>>({});

  /* Los rechazos del NAVEGADOR (tipo y peso), por recuadro. No son los del
     servidor —esos llegan como toast desde la pantalla, que es quien llama a
     la API—: son los que evitan mandar 10 MB por la red para enterarse
     después. La clave es la misma que `ocupadaEn`. */
  const [rechazos, setRechazos] = useState<Record<string, string>>({});

  /* La referencia de "lo guardado" es estado y no las props: después de un
     guardado exitoso la base tiene que moverse a lo que quedó en pantalla, y
     las props recién traen ese dato cuando vuelve el refetch. */
  const [base, setBase] = useState({
    deslindes: deslindesDesde(roles),
    notas: informe.notas?.descripcion ?? "",
    legales: legalesDesde(roles),
    periodo: informe.avaluo_fiscal_periodo ?? "",
  });

  const rellenar = useRellenarDescripcionConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);

  /** La corrida en curso y qué roles escribió, para poder descartarla o aceptarla. */
  const [ultimoRelleno, setUltimoRelleno] = useState<{
    ejecucionId: number;
    escritos: EscritoDeDescripcion[];
    /** Los datos legales que la IA escribió (campo por campo). */
    legalesEscritos: EscritoLegal[];
  } | null>(null);

  /**
   * Los deslindes de AHORA, para el `onSuccess` del relleno.
   *
   * LEER `deslindes` AHÍ SERÍA EL DE HACE VARIOS SEGUNDOS: leer el expediente
   * tarda, y quien apretó el botón sigue escribiendo mientras espera. Mismo
   * defecto que ya se corrigió en aguas y en suelos.
   */
  const deslindesAhora = useRef(deslindes);
  deslindesAhora.current = deslindes;

  /** Lo mismo para los datos legales: la lectura tarda y se sigue escribiendo. */
  const legalesAhora = useRef(legales);
  legalesAhora.current = legales;

  /** La corrida de AHORA, por lo mismo: un segundo clic la tiene que cerrar. */
  const ultimoRellenoAhora = useRef(ultimoRelleno);
  ultimoRellenoAhora.current = ultimoRelleno;

  /** El escrito de la IA en este rol, si su texto sigue intacto. */
  function escritoDelRol(rolId: number): EscritoDeDescripcion | undefined {
    const escrito = ultimoRelleno?.escritos.find((otro) => otro.id === rolId);

    return escrito && deslindeDe(rolId).texto === escrito.texto ? escrito : undefined;
  }

  const hayCambios =
    !mismosDeslindes(deslindes, base.deslindes) ||
    !mismosLegales(legales, base.legales) ||
    periodo !== base.periodo ||
    notas !== base.notas;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => {
      setBase({ deslindes, notas, legales, periodo });

      // EL GUARDADO ES LO QUE RESUELVE LA PROPUESTA, y no el clic que la
      // trajo: hasta acá era un borrador que la persona podía descartar. Se
      // avisa y se OLVIDA la corrida, para que un segundo guardado no vuelva a
      // resolverla. Es aceptada solo si quedó algo de lo que escribió: ver
      // `resultadoAlGuardar`.
      if (ultimoRelleno) {
        resolverElRelleno.mutate({
          ejecucionId: ultimoRelleno.ejecucionId,
          resultado: seUsoLoPropuesto(deslindes, legales, ultimoRelleno),
        });
        setUltimoRelleno(null);
        setAviso(null);
      }
    },
  }));

  /**
   * Un rol que se agregó en Antecedentes generales todavía no está en el
   * estado local, así que se lee con un valor por defecto en vez de indexar a
   * ciegas — `deslindes[id]` sería `undefined` y el textarea pasaría a no
   * controlado, que React avisa por consola y pierde lo escrito.
   */
  const deslindeDe = (id: number): DeslindesDelRol =>
    deslindes[id] ?? { texto: "", validada: false };

  function escribir(id: number, cambio: Partial<DeslindesDelRol>) {
    setDeslindes((actual) => ({ ...actual, [id]: { ...deslindeDe(id), ...cambio } }));
  }

  /** Un rol agregado en Antecedentes generales todavía no está en el estado local. */
  const legalesDe = (id: number): DatosLegalesDelRol => legales[id] ?? legalesVacios();

  function escribirLegal(id: number, campo: CampoLegal, valor: string) {
    setLegales((actual) => ({ ...actual, [id]: { ...(actual[id] ?? legalesVacios()), [campo]: valor } }));
    // Corregir el campo borra su aviso: ya no describe lo que hay escrito.
    setErroresLegales((actual) => {
      if (!actual[id]?.[campo]) return actual;

      const { [campo]: _quitado, ...resto } = actual[id];

      return { ...actual, [id]: resto };
    });
  }

  function elegirImagen(clave: string, archivo: File, subir: (archivo: File) => void) {
    const motivo = motivoDeRechazoDeLaImagen(archivo);

    setRechazos((actuales) => ({ ...actuales, [clave]: motivo ?? "" }));

    if (motivo === null) subir(archivo);
  }

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        // UN SEGUNDO CLIC REEMPLAZA AL PRIMERO. El texto de la corrida
        // anterior que nadie tocó se retira antes de mezclar la nueva, y la
        // corrida se cierra. Sin esto la primera quedaba pendiente para
        // siempre, y su texto ya no se podía descartar: "Descartar" solo
        // conocía la segunda.
        //
        // SE CIERRA CON LA MISMA REGLA QUE AL GUARDAR, no siempre como
        // descartada: lo que la persona corrigió sobrevive al retiro y se va a
        // guardar, así que esa corrida sirvió. Se evalúa DESPUÉS de retirar,
        // porque el texto intacto también está lleno y lo contaría como usado
        // justo cuando se lo está sacando.
        const anterior = ultimoRellenoAhora.current;
        const actual = anterior
          ? descartarLaPropuestaDeDescripcion(deslindesAhora.current, anterior.escritos)
          : deslindesAhora.current;

        const legalesActuales = anterior
          ? descartarLaPropuestaLegal(legalesAhora.current, anterior.legalesEscritos)
          : legalesAhora.current;

        if (anterior) {
          resolverElRelleno.mutate({
            ejecucionId: anterior.ejecucionId,
            resultado: seUsoLoPropuesto(actual, legalesActuales, anterior),
          });
        }

        const rolesIds = roles.map((rol) => rol.id);
        const mezcla = sumarLaPropuestaDeDescripcion(actual, propuesta.roles, rolesIds);
        // Los datos legales siguen la MISMA regla: solo campos vacíos.
        const mezclaLegal = sumarLaPropuestaLegal(legalesActuales, propuesta.roles, rolesIds);

        setDeslindes(mezcla.deslindes);
        setLegales(mezclaLegal.legales);

        // SI NO ESCRIBIÓ NADA, SE CIERRA EN EL ACTO como descartada: no hay
        // texto que la persona pueda aceptar al guardar ni que "Descartar"
        // tenga que deshacer, así que tampoco se ofrece ese botón.
        if (mezcla.escritos.length === 0 && mezclaLegal.escritos.length === 0) {
          resolverElRelleno.mutate({ ejecucionId: ejecucion_id, resultado: "descartado" });
          setUltimoRelleno(null);
        } else {
          setUltimoRelleno({
            ejecucionId: ejecucion_id,
            escritos: mezcla.escritos,
            legalesEscritos: mezclaLegal.escritos,
          });
        }

        setAviso({
          resumen: resumenDeLoPropuestoEnDescripcion(mezcla.escritos, mezclaLegal.escritos.length),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      // El error se muestra ACÁ y no en un toast: habla de esta pestaña y de
      // lo que hay que hacer para resolverlo, y un toast se va antes de
      // terminar de leerlo.
      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o completa la pestaña a mano.",
          ),
        ),
    });
  }

  /**
   * Revierte los roles cuyo texto sigue siendo el que trajo el último
   * relleno, y lo anota como descartado.
   *
   * UN DESCARTE INFORMA TANTO COMO UNA ACEPTACIÓN: sin él, `ia_ejecuciones`
   * solo tendría los casos en que la IA acertó, y esa proporción es lo único
   * que permite decidir si la función vale lo que cuesta.
   */
  function descartarElRelleno() {
    if (!ultimoRelleno) return;

    setDeslindes((actual) => descartarLaPropuestaDeDescripcion(actual, ultimoRelleno.escritos));
    setLegales((actual) => descartarLaPropuestaLegal(actual, ultimoRelleno.legalesEscritos));

    resolverElRelleno.mutate({
      ejecucionId: ultimoRelleno.ejecucionId,
      resultado: "descartado",
    });

    setUltimoRelleno(null);
    setAviso(null);
  }

  function guardar() {
    // Primero se lee lo legal: un valor ilegible se señala en SU campo y el
    // guardado no sale, o la persona creería que quedó algo que no quedó.
    const errores: Record<number, Partial<Record<CampoLegal, string>>> = {};
    const filasLegales = roles.map((rol) => {
      const resultado = legalesAPayload(legalesDe(rol.id));

      if (Object.keys(resultado.errores).length > 0) errores[rol.id] = resultado.errores;

      return resultado.valores;
    });

    setErroresLegales(errores);
    if (Object.keys(errores).length > 0) return;

    onGuardar(
      roles.map((rol, indice) => {
        const { texto, validada } = deslindeDe(rol.id);

        return {
          id: rol.id,
          // Vaciar el textarea BORRA la descripción: `""` viaja como `null`,
          // que es lo que la columna guarda cuando no hay prosa.
          descripcion_deslindes: texto.trim() === "" ? null : texto,
          descripcion_deslindes_validada: validada,
          ...filasLegales[indice],
        };
      }),
      notas,
      periodo.trim() === "" ? null : periodo.trim(),
    );
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setDeslindes(base.deslindes);
    setLegales(base.legales);
    setPeriodo(base.periodo);
    setErroresLegales({});
    setNotas(base.notas);

    if (ultimoRelleno) {
      resolverElRelleno.mutate({ ejecucionId: ultimoRelleno.ejecucionId, resultado: "descartado" });
      setUltimoRelleno(null);
    }

    setAviso(null);
  }

  const validadas = roles.filter((rol) => deslindeDe(rol.id).validada).length;
  const unidad = informe.unidad_superficie === "m2" ? "m²" : "ha";

  /* LA COLUMNA DE LA DERECHA LEE LO QUE HAY EN PANTALLA, no lo guardado: se
     mueve con cada tecla, igual que la de Antecedentes generales. */
  const entradaDelIndice: EntradaDeDescripcion = {
    roles: roles.map((rol) => ({
      id: rol.id,
      rol_sii: rol.rol_sii,
      comuna: rol.comuna,
      texto: deslindeDe(rol.id).texto,
      validada: deslindeDe(rol.id).validada,
      hayImagen: Boolean(rol.imagen),
    })),
    imagenes: {
      limites_generales: Boolean(informe.imagenes?.limites_generales),
      vias_acceso: Boolean(informe.imagenes?.vias_acceso),
      vista_alejada: Boolean(informe.imagenes?.vista_alejada),
    },
    notas,
  };

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_15.125rem]">
        <div className="flex min-w-0 flex-col gap-3.5">
          {/* El botón va en la BANDA y no junto al guardado: rellenar no guarda
              nada, y ponerlo al lado de "Guardar cambios" invitaría a leerlo
              como parte del mismo gesto. */}
          <section className="border-success-dot/25 bg-success-soft/50 flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-4 py-3">
            <span className="bg-success-soft text-success-soft-foreground flex size-8.5 flex-none items-center justify-center rounded-lg">
              <SparklesIcon className="size-4" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-ink text-[0.8125rem] font-bold">Redactar los deslindes con IA</h3>
              <p className="text-ink-muted mt-0.5 text-[0.71875rem] font-semibold text-pretty">
                Completa solo los roles sin texto. No valida nada: revise cada rol antes de marcarlo.
              </p>
              {roles.length === 0 ? (
                // El botón deshabilitado no puede quedar mudo: sin roles no
                // hay a quién escribirle.
                <p className="text-ink-hint mt-0.5 text-[0.71875rem] font-semibold">
                  Carga roles en Antecedentes generales para poder rellenar.
                </p>
              ) : null}
            </div>
            <div className="flex-none">
              <BotonDeRellenoConIa
                rellenando={rellenar.isPending}
                disabled={guardando || roles.length === 0}
                variante="oscura"
                onRellenar={pedirElRelleno}
              />
            </div>
          </section>

          <AvisoDeLoRellenado
            rellenando={rellenar.isPending}
            error={errorDelRelleno}
            aviso={aviso}
            onDescartar={ultimoRelleno ? descartarElRelleno : null}
          />

          <section
            id={idDeLaSeccionDeDescripcion("limites")}
            className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
          >
            <EncabezadoNumerado
              numero="01"
              idDelTitulo="descripcion-limites-titulo"
              titulo="Límites de cada rol"
              bajada="Imagen del límite y deslindes de cada rol según escritura."
              acciones={
                roles.length > 0 ? (
                  // El resumen mira las descripciones VALIDADAS y no las
                  // escritas: lo que hay que saber de un vistazo es cuánto
                  // queda por revisar, porque el texto lo puede haber
                  // propuesto la IA.
                  <span
                    className={cn(
                      "rounded-[0.4375rem] px-2.5 py-[0.3125rem] font-mono text-[0.6875rem] font-bold",
                      validadas === roles.length
                        ? "bg-success-soft text-success-soft-foreground"
                        : "bg-muted text-ink-muted",
                    )}
                  >
                    {validadas} de {roles.length} {roles.length === 1 ? "validado" : "validados"}
                  </span>
                ) : undefined
              }
            />

            {/* UN BLOQUE POR ROL. Sin roles no se dibuja una lista vacía: se
                explica dónde se cargan, que es la otra pestaña. */}
            {roles.length === 0 ? (
              <p className="bg-muted/40 text-ink-muted mt-3.5 rounded-xl p-4 text-sm">
                Este informe todavía no tiene roles cargados. Se agregan en Antecedentes generales,
                y cada uno abre acá su bloque de límites.
              </p>
            ) : (
              <div className="mt-3.5 flex flex-col gap-3">
                {/* El semestre es UNO por informe: rotula la columna «Avalúo
                    fiscal» de la tabla legal de todos los roles. */}
                <div className="flex max-w-[20rem] flex-col gap-1">
                  <Label htmlFor="avaluo-fiscal-periodo" className="text-ink-body text-[0.71875rem] font-bold">
                    Semestre del avalúo fiscal
                  </Label>
                  <Input
                    id="avaluo-fiscal-periodo"
                    value={periodo}
                    onChange={(e) => setPeriodo(e.target.value)}
                    disabled={guardando}
                    placeholder="Ej.: 1er. Sem. 2026"
                    maxLength={60}
                  />
                </div>

                {roles.map((rol) => {
                  const { texto, validada } = deslindeDe(rol.id);
                  const detalle = [rol.comuna, rol.superficie ? `${rol.superficie} ${unidad}` : null]
                    .filter(Boolean)
                    .join(" · ");
                  const escrito = escritoDelRol(rol.id);

                  return (
                    <div
                      key={rol.id}
                      id={idDelRolDeDescripcion(rol.id)}
                      className={cn(
                        "flex scroll-mt-5 flex-col gap-2.5 rounded-xl border-[1.5px] px-3.5 pt-3 pb-3.5",
                        validada ? "border-success-dot/40" : "border-border",
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-2.5">
                        <span className="text-ink text-[0.8125rem] font-bold">
                          Rol <span className="font-mono">{rol.rol_sii}</span>
                        </span>
                        {detalle ? (
                          <span className="text-ink-muted text-xs font-semibold">{detalle}</span>
                        ) : null}
                      </div>

                      <div className="grid items-stretch gap-3.5 md:grid-cols-[16.25rem_minmax(0,1fr)]">
                        <div className="flex min-w-0 flex-col gap-1.5">
                          <span className="text-ink-body text-[0.71875rem] font-bold">
                            Imagen del límite
                          </span>
                          <TarjetaDeImagen
                            plana
                            claseDelRecuadro="h-[10.625rem] w-full"
                            titulo={tituloDelRol(rol)}
                            marcador="Límite del rol · JPG, PNG o WEBP"
                            src={rol.imagen ? urlDeLaImagenDelRol(informe.id, rol.id, rol.imagen.hash) : null}
                            mime={rol.imagen?.mime}
                            peso={rol.imagen?.bytes}
                            ocupado={ocupadaEn === claveDelRol(rol.id)}
                            error={rechazos[claveDelRol(rol.id)] || null}
                            onArchivo={(archivo) =>
                              elegirImagen(claveDelRol(rol.id), archivo, (a) => onImagenDelRol(rol.id, a))
                            }
                            onQuitar={() => onQuitarImagenDelRol(rol.id)}
                          />
                        </div>

                        <div className="flex min-w-0 flex-col gap-1.5">
                          <Label
                            htmlFor={`deslindes-${rol.id}`}
                            className="text-ink-body text-[0.71875rem] font-bold"
                          >
                            Descripción de deslindes
                            <span className="sr-only"> del rol {rol.rol_sii}</span>
                          </Label>
                          <CampoConAsistencia
                            id={`deslindes-${rol.id}`}
                            campo={CAMPOS_CON_ASISTENCIA.descripcionDeslindes}
                            value={texto}
                            onChange={(nuevo) => escribir(rol.id, { texto: nuevo })}
                            informeId={informe.id}
                            rows={6}
                            llenarAlto
                            placeholder="Ej.: Norte: con camino público vecinal de acceso al sector, en 1.240 m…"
                            disabled={guardando}
                            accionesDespues={
                              // POR ROL, no al pie de la pestaña: dice "yo
                              // revisé ESTA descripción", que es lo único que
                              // distingue un texto leído de uno que salió de
                              // la IA y nadie miró.
                              <>
                                <PildoraDeValidacion
                                  validada={validada}
                                  etiqueta={`Información validada del rol ${rol.rol_sii}`}
                                  disabled={guardando}
                                  onValidada={(v) => escribir(rol.id, { validada: v })}
                                />
                                <span className="text-ink-hint text-[0.6875rem] font-semibold">
                                  {validada
                                    ? "Revisada por una persona."
                                    : "Márcala cuando hayas revisado el texto."}
                                </span>
                              </>
                            }
                          />

                          {/* Solo mientras el texto siga siendo el que la IA
                              escribió: si la persona lo cambia, la fuente ya no
                              lo describe. */}
                          {escrito ? <FuenteDeLaPropuesta fuente={escrito.fuente} /> : null}
                        </div>
                      </div>

                      {/* LOS DATOS LEGALES del rol: la fila de la tabla con
                          que abre el capítulo legal del informe. */}
                      <fieldset className="border-border flex min-w-0 flex-col gap-2.5 rounded-lg border-[1.5px] px-3 pt-1 pb-3">
                        <legend className="rotulo-seccion px-1">Antecedentes legales</legend>
                        <div className="grid gap-x-3 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-4">
                          {CAMPOS_DEL_FORMULARIO_LEGAL.map(({ campo, rotulo, ancho }) => {
                            const idDelCampo = `legal-${rol.id}-${campo}`;
                            const error = erroresLegales[rol.id]?.[campo];
                            const etiqueta = rotulo.replace("{unidad}", unidad);

                            return (
                              <div
                                key={campo}
                                className={cn("flex min-w-0 flex-col gap-1", ancho === "doble" && "sm:col-span-2")}
                              >
                                <Label htmlFor={idDelCampo} className="text-ink-body text-[0.71875rem] font-bold">
                                  {etiqueta}
                                  <span className="sr-only"> del rol {rol.rol_sii}</span>
                                </Label>
                                <Input
                                  id={idDelCampo}
                                  value={legalesDe(rol.id)[campo]}
                                  onChange={(e) => escribirLegal(rol.id, campo, e.target.value)}
                                  disabled={guardando}
                                  aria-invalid={error ? true : undefined}
                                  aria-describedby={error ? `${idDelCampo}-error` : undefined}
                                  inputMode={campo.startsWith("superficie") || campo === "avaluo_fiscal_clp" || campo === "inscripcion_anio" ? "decimal" : undefined}
                                />
                                {error ? (
                                  <p id={`${idDelCampo}-error`} className="text-danger-texto text-meta">
                                    {error}
                                  </p>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-ink-hint text-[0.6875rem] font-semibold">
                          La superficie de planimetría se mide sobre el plano: la IA no la propone. Fojas y
                          número admiten letras (7705V, vta).
                        </p>
                      </fieldset>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* LOS TRES ENCUADRES FIJOS. Van después de los roles porque
              describen el predio ENTERO: se leen una vez que ya se sabe de
              cuántas partes está hecho. */}
          {IMAGENES_DEL_EXPEDIENTE.map(({ tipo, etiqueta }, indice) => {
            const imagen = informe.imagenes?.[tipo] ?? null;
            const clave = SECCION_DEL_ENCUADRE[tipo as keyof typeof SECCION_DEL_ENCUADRE];
            const detalle = DETALLE_DEL_ENCUADRE[tipo];

            return (
              <section
                key={tipo}
                id={idDeLaSeccionDeDescripcion(clave)}
                className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
              >
                <EncabezadoNumerado
                  numero={`0${2 + indice}`}
                  idDelTitulo={`descripcion-${clave}-titulo`}
                  titulo={etiqueta}
                  bajada={
                    tipo === "limites_generales"
                      ? (notaDeLimitesGenerales(roles.length) ?? detalle.bajada)
                      : detalle.bajada
                  }
                  acciones={
                    <span className="bg-muted text-ink-muted rounded-[0.4375rem] px-2.5 py-[0.3125rem] text-[0.65625rem] font-bold">
                      Se guarda al soltarla
                    </span>
                  }
                />
                <div className="mt-3.5">
                  <TarjetaDeImagen
                    plana
                    claseDelRecuadro="h-[14.375rem] max-w-[28.75rem]"
                    titulo={etiqueta}
                    marcador={detalle.marcador}
                    src={imagen ? urlDeLaImagenDelExpediente(informe.id, tipo, imagen.hash) : null}
                    mime={imagen?.mime}
                    peso={imagen?.bytes}
                    ocupado={ocupadaEn === tipo}
                    error={rechazos[tipo] || null}
                    onArchivo={(archivo) =>
                      elegirImagen(tipo, archivo, (a) => onImagenDelExpediente(tipo, a))
                    }
                    onQuitar={() => onQuitarImagenDelExpediente(tipo)}
                  />
                </div>
              </section>
            );
          })}

          {/* LOS ANEXOS (planos y galerías con leyenda) se guardan solos: no
              entran en el conteo de cambios de la barra de abajo. */}
          <AnexosDelInforme informe={informe} />

          <section
            id={idDeLaSeccionDeDescripcion("notas")}
            className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
          >
            <EncabezadoNumerado
              numero="05"
              idDelTitulo="descripcion-notas-titulo"
              titulo="Notas u observaciones"
              bajada="Opcional."
            />
            <div className="mt-3.5">
              <NotasDeLaPestana
                id="notas-descripcion"
                value={notas}
                onChange={setNotas}
                disabled={guardando}
                placeholder="Ej.: considerar que…"
                plana
              />
            </div>
          </section>
        </div>

        <IndiceLateral
          titulo="Descripción de la propiedad"
          etiquetaDeLaNavegacion="Secciones de la descripción de la propiedad"
          entradas={entradasDelIndiceDeDescripcion(entradaDelIndice)}
          avance={avanceDeDescripcion(seccionesDeDescripcionCompletas(entradaDelIndice))}
          pendientes={pendientesDeDescripcion(entradaDelIndice)}
          onIr={irAlDestino}
        />
      </div>

      {/* LA BARRA VA AL PIE de la pestaña, como en Suelos y Construcciones.
          Cuenta lo que es de la PESTAÑA (textos, validaciones y notas); las
          imágenes no entran porque ya están guardadas. */}
      <BarraDeGuardado
        cambios={
          contarCambiosDeDescripcion(deslindes, base.deslindes, notas !== base.notas) +
          contarLegalesCambiados(legales, base.legales) +
          (periodo !== base.periodo ? 1 : 0)
        }
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las imágenes se guardan solas al soltarlas."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />
    </div>
  );
}

/**
 * Lleva la vista a una tarjeta o a un rol. Es un botón con scroll y no un
 * `#ancla`: un enlace de hash cambia la url, y la guardia de cambios sin
 * guardar escucha esa navegación.
 */
function irAlDestino(destino: string) {
  document.getElementById(destino)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
}

/** Lo propio de cada encuadre: su bajada y el texto del recuadro vacío. */
const DETALLE_DEL_ENCUADRE: Record<string, { bajada: string; marcador: string }> = {
  limites_generales: {
    bajada: "El polígono del predio completo, con todos sus roles dentro.",
    marcador: "Polígono de la propiedad completa · JPG, PNG o WEBP",
  },
  vias_acceso: {
    bajada: "Escala cercana: camino de acceso, cruces y poblados a pocos kilómetros.",
    marcador: "Mapa de accesos · JPG, PNG o WEBP",
  },
  vista_alejada: {
    bajada: "Escala regional: posición del predio respecto de las ciudades principales.",
    marcador: "Mapa regional · JPG, PNG o WEBP",
  },
  // Las otras tres no se dibujan acá: son de la pestaña de suelos y la portada.
  mosaico_suelos: { bajada: "", marcador: "" },
  plano_regulador: { bajada: "", marcador: "" },
  portada: { bajada: "", marcador: "" },
};

/**
 * Los campos legales de cada rol, en el orden de la tabla del informe. El
 * rótulo lleva `{unidad}` donde la unidad es la del informe: la misma en las
 * tres superficies.
 */
const CAMPOS_DEL_FORMULARIO_LEGAL: { campo: CampoLegal; rotulo: string; ancho?: "doble" }[] = [
  { campo: "nombre_predio", rotulo: "Predio / lote", ancho: "doble" },
  { campo: "destino_sii", rotulo: "Destino SII", ancho: "doble" },
  { campo: "superficie_titulos", rotulo: "Superficie títulos ({unidad})" },
  { campo: "superficie_sii", rotulo: "Superficie SII ({unidad})" },
  { campo: "superficie_planimetria", rotulo: "Superficie planimetría ({unidad})" },
  { campo: "avaluo_fiscal_clp", rotulo: "Avalúo fiscal ($)" },
  { campo: "inscripcion_fojas", rotulo: "Fojas" },
  { campo: "inscripcion_numero", rotulo: "Número" },
  { campo: "inscripcion_anio", rotulo: "Año" },
  { campo: "inscripcion_cbr", rotulo: "Conservador (CBR)" },
];

/** El estado inicial de los datos legales, leído de los roles que trae la ficha. */
function legalesDesde(roles: RolDeInforme[]): LegalesPorRol {
  return Object.fromEntries(roles.map((rol) => [rol.id, legalesDelRol(rol)]));
}

/**
 * Cómo se resuelve la corrida: ACEPTADA si queda algo de lo que la IA escribió,
 * sean deslindes o datos legales; DESCARTADA si la persona lo vació todo.
 */
function seUsoLoPropuesto(
  deslindes: DeslindesPorRol,
  legales: LegalesPorRol,
  corrida: { escritos: EscritoDeDescripcion[]; legalesEscritos: EscritoLegal[] },
): "aceptado" | "descartado" {
  return resultadoAlGuardar(deslindes, corrida.escritos) === "aceptado" ||
    quedaAlgoDeLoLegalPropuesto(legales, corrida.legalesEscritos)
    ? "aceptado"
    : "descartado";
}

/** Cuántos roles cambiaron de texto o de casilla, más uno si cambiaron las notas. */
function contarCambiosDeDescripcion(
  a: DeslindesPorRol,
  b: DeslindesPorRol,
  notasCambiaron: boolean,
): number {
  const vacio: DeslindesDelRol = { texto: "", validada: false };
  const claves = new Set([...Object.keys(a), ...Object.keys(b)].map(Number));
  const roles = [...claves].filter((id) => {
    const uno = a[id] ?? vacio;
    const otro = b[id] ?? vacio;

    return uno.texto !== otro.texto || uno.validada !== otro.validada;
  }).length;

  return roles + (notasCambiaron ? 1 : 0);
}

/**
 * Cómo se rotula el bloque de un rol: su número y su comuna.
 *
 * La comuna va al lado del número porque el rol es único DENTRO de su comuna,
 * no en el informe: hay predios cuyos roles caen en comunas distintas, y dos
 * comunas pueden usar el mismo número para parcelas diferentes. Sin ella, dos
 * bloques podrían llamarse igual.
 */
function tituloDelRol(rol: RolDeInforme): string {
  return rol.comuna ? `Límite del rol ${rol.rol_sii} · ${rol.comuna}` : `Límite del rol ${rol.rol_sii}`;
}

/** La clave con la que se identifica el recuadro de un rol entre los fijos. */
function claveDelRol(rolId: number): string {
  return `rol-${rolId}`;
}

/**
 * La aclaración de "Límites generales", que solo tiene sentido con UN rol.
 *
 * Con un solo rol esa imagen suele ser la misma que la de su límite, y decirlo
 * evita que alguien busque una captura distinta que no existe. Con varios, la
 * imagen general es otra cosa —el predio completo— y la aclaración sería falsa.
 */
function notaDeLimitesGenerales(cuantosRoles: number): string | undefined {
  return cuantosRoles === 1
    ? "Como la propiedad tiene un solo rol, esta imagen suele ser la misma que el límite de ese rol."
    : undefined;
}

/** El estado inicial de la pestaña, leído de los roles que trae la ficha. */
function deslindesDesde(roles: RolDeInforme[]): DeslindesPorRol {
  return Object.fromEntries(
    roles.map((rol) => [
      rol.id,
      {
        // `null` se vuelve `""`: un `<textarea value={null}>` pasa a no
        // controlado y React avisa por consola, además de perder el valor al
        // primer tipeo.
        texto: rol.descripcion_deslindes ?? "",
        validada: rol.descripcion_deslindes_validada ?? false,
      },
    ]),
  );
}

/**
 * Dos estados son iguales si coinciden rol por rol.
 *
 * No sirve comparar cantidad de claves: escribir en un rol que no estaba en la
 * base —uno agregado en la otra pestaña— deja una clave más describiendo el
 * mismo vacío.
 */
function mismosDeslindes(a: DeslindesPorRol, b: DeslindesPorRol): boolean {
  const vacio: DeslindesDelRol = { texto: "", validada: false };
  const claves = new Set([...Object.keys(a), ...Object.keys(b)].map(Number));

  return [...claves].every((id) => {
    const uno = a[id] ?? vacio;
    const otro = b[id] ?? vacio;

    return uno.texto === otro.texto && uno.validada === otro.validada;
  });
}
